import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Hourglass } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AGB_URL } from "@/components/LegalFooter";

const FIELDS = [
  ["first_name", "Vorname", "text", "given-name"],
  ["last_name", "Nachname", "text", "family-name"],
  ["email", "E-Mail-Adresse", "email", "email"],
  ["password", "Passwort", "password", "new-password"],
  ["password_confirm", "Passwort bestätigen", "password", "new-password"],
];

export default function Register() {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", password_confirm: "" });
  const [zustimmung, setZustimmung] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.password.length < 8) return setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
    if (f.password !== f.password_confirm) return setError("Die Passwörter stimmen nicht überein.");
    if (!zustimmung) return setError("Bitte bestätigen Sie die gewerbliche Nutzung und akzeptieren Sie die AGB und die Datenschutzerklärung.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...f, terms_accepted: true });
      setDone(data.message);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Neu registrieren" subtitle="Beantragen Sie ein Benutzerkonto.">
      {done ? (
        <div className="text-center space-y-4" data-testid="register-confirmation">
          <div className="mx-auto h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center">
            <Hourglass className="h-7 w-7 text-amber-600" />
          </div>
          <p className="text-slate-600">{done}</p>
          <Link to="/login" className="inline-block text-primary font-semibold hover:underline" data-testid="register-back-login">Zurück zur Anmeldung</Link>
        </div>
      ) : (
        <>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(([k, label, type, ac]) => (
                <div key={k} className={k.endsWith("name") ? "" : "col-span-2"}>
                  <Label htmlFor={`reg-${k}`}>{label}</Label>
                  <Input id={`reg-${k}`} type={type} autoComplete={ac} required value={f[k]}
                    onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5 h-11" data-testid={`register-${k.replace("_", "-")}-input`} />
                </div>
              ))}
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <Checkbox id="reg-terms" checked={zustimmung} onCheckedChange={(v) => setZustimmung(v === true)} className="mt-0.5" data-testid="register-terms-checkbox" />
              <label htmlFor="reg-terms" className="text-sm text-slate-700 leading-snug">
                Ich nutze PrüfDoc Pro gewerblich und akzeptiere die{" "}
                <a href={AGB_URL} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold underline" data-testid="register-agb-link">AGB</a>{" "}
                und die{" "}
                <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold underline" data-testid="register-datenschutz-link">Datenschutzerklärung</a>.
              </label>
            </div>
            {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
            <Button type="submit" className="w-full h-11" disabled={loading || !zustimmung} data-testid="register-submit-btn">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Registrierung absenden"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-500">
            Schon ein Konto? <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="register-login-link">Zur Anmeldung</Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}
